<template>
  <div style="min-height: var(--app-viewport-height, 100vh); background: linear-gradient(180deg, #EDF3FD 0%, #F8FAFC 100%);">
    <!-- Loader -->
    <Loader :loading="showLoader" />

    <v-container fluid class="py-4 py-md-6">
      <v-row justify="center">
        <v-col cols="12" lg="11" xl="10">
          <!-- Header Toolbar -->
          <v-sheet color="transparent">
            <v-card
              rounded="xl"
              elevation="4"
              class="px-4 px-md-6 py-4"
              style="background: linear-gradient(135deg, #285096 0%, #23A5E6 50%, #59C4FF 100%);"
            >
              <div class="d-flex align-center">
                <div class="logo-wrapper">
                  <img :src="logo" alt="Pinestack Logo" class="header-logo" />
                </div>
                
                <div>
                  <div class="text-h6 font-weight-semibold" style="color: #ffffff;">
                    {{ $t('chat.contact') }}
                  </div>
                </div>

                <v-spacer />

                <v-btn
                  icon
                  variant="text"
                  color="white"
                  :loading="isRefreshing"
                  @click="handleRefresh"
                >
                  <v-icon size="24">mdi-refresh</v-icon>
                </v-btn>
              </div>
            </v-card>
          </v-sheet>

          <!-- Main Content -->
          <v-sheet color="transparent" class="mt-4">
            <v-card color="white" elevation="2" rounded="xl" class="pa-4 pa-md-6">
              <!-- Search Bar -->
              <v-row align="center" class="mb-4" no-gutters>
                <v-col cols="12" sm="8" md="6" class="pr-sm-3">
                  <v-text-field
                    v-model="searchQuery"
                    :placeholder="$t('chat.searchbar-text')"
                    prepend-inner-icon="mdi-magnify"
                    variant="solo-filled"
                    density="comfortable"
                    hide-details
                    clearable
                    rounded="lg"
                    base-color="#9AA6B1"
                    color="#23A5E6"
                  />
                </v-col>

                <v-spacer />

                <v-col cols="auto">
                  <span
                    v-if="filteredUsers.length"
                    class="text-caption text-md-body-2"
                    style="color: #718096;"
                  >
                    Showing {{ filteredUsers.length }} {{ filteredUsers.length === 1 ? 'contact' : 'contacts' }}
                  </span>
                </v-col>
              </v-row>

              <!-- Error State -->
              <v-alert
                v-if="errorMessage"
                type="error"
                variant="tonal"
                class="mb-4"
                closable
                rounded="lg"
                @click:close="errorMessage = ''"
              >
                {{ errorMessage }}
              </v-alert>

              <!-- Loading State -->
              <div v-if="showLoader" class="d-flex flex-column align-center justify-center py-16">
                <v-progress-circular indeterminate color="#23A5E6" size="56" width="4" />
                <p class="text-body-1 mt-4" style="color: #4E5965;">
                  Loading contacts...
                </p>
              </div>

              <!-- Empty State -->
              <v-card
                v-else-if="filteredUsers.length === 0"
                variant="outlined"
                rounded="xl"
                class="pa-12 text-center"
              >
                <v-icon size="96" color="#B7BFC7">mdi-account-search-outline</v-icon>
                <h3 class="text-h6 mt-4 font-weight-semibold" style="color: #373F47;">
                  No contacts found
                </h3>
                <p class="text-body-2 mt-2" style="color: #9AA6B1;">
                  Try adjusting your search criteria
                </p>
              </v-card>

              <!-- User List -->
              <v-row v-else dense>
                <v-col
                  v-for="(item, index) in filteredUsers"
                  :key="index"
                  cols="12"
                  sm="6"
                  lg="4"
                  xl="3"
                >
                  <v-card
                    :disabled="item.email === ownUser?.email"
                    elevation="2"
                    class="user-card"
                    rounded="xl"
                    :class="{ 'user-card--disabled': item.email === ownUser?.email }"
                    @click="item.email !== ownUser?.email && openChat(item)"
                    hover
                  >
                    <div class="d-flex align-center pa-4">
                      <!-- Avatar -->
                      <v-avatar size="56" class="mr-3 avatar-border">
                        <v-img :src="item.prependAvatar" :alt="item.title" />
                      </v-avatar>

                      <!-- User Info -->
                      <div class="flex-grow-1 overflow-hidden">
                        <div class="text-subtitle-1 font-weight-semibold text-truncate" v-html="item.title" />
                        <div class="text-caption text-truncate mt-1" style="color: #718096;" v-html="item.subtitle" />
                      </div>

                      <!-- Favorite Button -->
                      <v-btn
                        v-if="item.email !== ownUser?.email"
                        icon
                        variant="text"
                        size="small"
                        class="favorite-btn ml-2"
                        @click.stop="toggleFavorite(item)"
                      >
                        <v-icon :color="item.isFavorite ? '#fc8e17' : '#B7BFC7'">
                          {{ item.isFavorite ? 'mdi-star' : 'mdi-star-outline' }}
                        </v-icon>
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      location="bottom right"
      color="#285096"
      rounded="lg"
    >
      <div class="d-flex align-center ga-2 font-weight-medium">
        <v-icon size="20">mdi-check-circle</v-icon>
        {{ snackbarText }}
      </div>
      <template v-slot:actions>
        <v-btn variant="text" class="text-none" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { ref, defineComponent, onMounted, computed, watch } from 'vue'
import { debounce } from 'lodash'
import Loader from '@/components/general/Loader.vue'
import { getToolbarLogoImage } from '@/assets/Branding/branding.js'
import { getUsers } from '@/controllers/BackboneAPI'
import { getUserProfiles, getFile, getDataFromBlob } from '@/controllers/BaseController'
import user_avatar from '@/assets/General/user_avatar.png'
import user_avatar2 from '@/assets/General/user_avatar2.png'
import { storeBlob, retrieveBlob } from '@/services/utils'
import { useRouter } from 'vue-router'
import { useOAuthService } from '@/services/OAuthService'

export default defineComponent({
  name: 'ChatUsersList',
  components: {
    Loader,
  },
  setup() {
    const logo = ref('')
    const OAuthService = useOAuthService()
    const showLoader = ref(false)
    const isRefreshing = ref(false)
    const users = ref([])
    const ownUser = ref(null)
    const searchQuery = ref('')
    const debouncedSearchQuery = ref('')
    const snackbar = ref(false)
    const snackbarText = ref('')
    const errorMessage = ref('')
    const router = useRouter()

    const openChat = async (user) => {
      try {
        const userCopy = JSON.parse(JSON.stringify(user))
        await storeBlob('selectedUser', userCopy)
        await storeBlob('ownUser', JSON.parse(JSON.stringify(ownUser.value)))
        router.push({
          name: 'Chat',
          params: { name: user.title, email: user?.email },
        })
      } catch (err) {
        console.error('Failed to open chat:', err)
        errorMessage.value = 'Failed to open chat. Please try again.'
      }
    }

    const filteredUsers = computed(() => {
      const query = debouncedSearchQuery.value.toLowerCase()
      const currentUser = JSON.parse(JSON.stringify(ownUser.value))
      const currentEmail = currentUser?.email
      const chatUsers = [
        currentEmail,
        'maximilian.hammer@pinestack.io',
        'graphics@pinestack.io',
        'qa@pinestack.io',
        'wilfried.hauck@pinestack.io',
        'khizar.alam@pinestack.io',
        'anny.zahid@pinestack.io',
        'gyuri.papp@pinestack.io',
      ]

      return users.value
        .filter((user) => user && user.title && user.subtitle && user.prependAvatar && chatUsers.includes(user.email))
        .filter(
          (user) =>
            user.title.toLowerCase().includes(query) ||
            user.subtitle.toLowerCase().includes(query) ||
            (user.profile?.job?.toLowerCase().includes(query) ?? false)
        )
        .sort((a, b) => {
          if (a.isFavorite && !b.isFavorite) return -1
          if (!a.isFavorite && b.isFavorite) return 1
          return a?.title?.localeCompare(b.title)
        })
    })

    const updateSearchQuery = debounce((query) => {
      debouncedSearchQuery.value = query
    }, 300)

    watch(searchQuery, (newQuery) => {
      updateSearchQuery(newQuery)
    })

    const toggleFavorite = async (user) => {
      try {
        user.isFavorite = !user.isFavorite
        if (user.isFavorite) {
          await storeBlob(user?.email, true)
          snackbarText.value = `${user.title} added to favorites`
        } else {
          await storeBlob(user?.email, false)
          snackbarText.value = `${user.title} removed from favorites`
        }
        snackbar.value = true
        sortUsers()
      } catch (err) {
        console.error('Error toggling favorite:', err)
        errorMessage.value = 'Failed to update favorites. Please try again.'
      }
    }

    const sortUsers = () => {
      users.value = users.value.slice().sort((a, b) => {
        if (a.isFavorite && !b.isFavorite) return -1
        if (!a.isFavorite && b.isFavorite) return 1
        return a?.title?.localeCompare(b.title)
      })
    }

    const handleRefresh = async () => {
      isRefreshing.value = true
      try {
        await initializeUsers()
      } catch (err) {
        console.error('Error refreshing users:', err)
        errorMessage.value = 'Failed to refresh contacts. Please try again.'
      } finally {
        isRefreshing.value = false
      }
    }

    const initializeUsers = async () => {
      try {
        logo.value = await getToolbarLogoImage()
        showLoader.value = true
        showLoader.value = true
        const ownUserProfile = await OAuthService.getUserProfile()

        let usersList = await getUsers()
        usersList = usersList.filter(
          (user) =>
            user?.firstName === undefined ||
            user?.lastName === undefined ||
            (user?.firstName !== 'firstname' && user?.lastName !== 'lastname')
        )

        const [userProfiles] = await Promise.all([getUserProfiles()])

        let formattedUsers = await Promise.all(
          usersList.map(async (user) => {
            const email = user?.email
            let profile = null
            let prependAvatar = user_avatar
            let title = email?.split('@')[0] || ''
            let subtitle = email

            try {
              profile = userProfiles.find((p) => p?.email === email)
              if (
                user?.firstName &&
                user?.lastName &&
                user?.firstName.toLowerCase() !== 'firstname' &&
                user?.lastName.toLowerCase() !== 'lastname'
              ) {
                title = `${user.firstName} ${user.lastName}`
              } else {
                title = title.replace(/\./g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
              }

              subtitle = profile?.job ? `${email}<br><span class="text-primary">Position</span> &mdash; ${profile.job}` : email

              if (profile) {
                const profileImage = profile?.files?.entities.find((f) => f.type === 'profileImage')
                if (profileImage) {
                  try {
                    const fileData = await getFile(profileImage.id)
                    const base64 = await getDataFromBlob(fileData)
                    if (base64.startsWith('data:image')) prependAvatar = base64
                  } catch (e) {
                    console.warn(`Failed loading profile image for ${email}:`, e)
                  }
                } else {
                  prependAvatar = profile?.email === ownUserProfile?.email ? user_avatar2 : user_avatar
                }
              }
            } catch (e) {
              console.warn(`Failed processing user ${email}:`, e)
            }

            let isFavorite = false
            try {
              isFavorite = (await retrieveBlob(email)) === true
            } catch (e) {
              console.warn(`Failed to retrieve favorite status for ${email}:`, e)
            }

            return {
              ...user,
              profile,
              title,
              subtitle,
              prependAvatar,
              isFavorite,
            }
          })
        )

        formattedUsers = formattedUsers.filter(
          (user) =>
            !['formobweb@yopmail.com', 'pinestackuser@gmail.com', 'blueidtest@pinestack.io', 'wha02@outlook.com'].includes(
              user.email
            ) &&
            (ownUserProfile.email === 'smkhizar.alam@gmail.com'
              ? user.email !== 'khizar.alam@pinestack.io'
              : user.email !== 'smkhizar.alam@gmail.com')
        )

        formattedUsers.forEach((user) => {
          if (user.email === 'graphics@pinestack.io') {
            user.title = 'Liana Amroyan'
            user.firstName = 'Liana'
            user.lastName = 'Amroyan'
          } else if (user.email === 'qa@pinestack.io') {
            user.title = 'Meri Mkrtchyan'
            user.firstName = 'Meri'
            user.lastName = 'Mkrtchyan'
          }
        })

        ownUser.value = formattedUsers.find((u) => u.email === ownUserProfile.email)
        users.value = formattedUsers
        sortUsers()
      } catch (error) {
        console.error('Error initializing ChatUsersList:', error)
        errorMessage.value = 'Failed to load users. Please refresh the page.'
      } finally {
        showLoader.value = false
      }
    }

    onMounted(async () => {
      await initializeUsers()
    })

    return {
      logo,
      users,
      showLoader,
      isRefreshing,
      searchQuery,
      filteredUsers,
      snackbar,
      snackbarText,
      errorMessage,
      toggleFavorite,
      openChat,
      ownUser,
      handleRefresh,
    }
  },
})
</script>

<style scoped>
.logo-wrapper {
  width: 52px;
  height: 52px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin-right: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.logo-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
}

.header-logo {
  height: 32px;
  width: auto;
  object-fit: contain;
  display: block;
}

.user-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
}

.user-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(40, 80, 150, 0.12) !important;
  border-color: rgba(40, 80, 150, 0.2);
}

.user-card:active {
  transform: translateY(0px);
  transition: all 0.1s;
}

.user-card--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.avatar-border {
  border: 2px solid #E2E8F0;
  transition: border-color 0.3s ease;
}

.user-card:hover .avatar-border {
  border-color: #5280D1;
}

.favorite-btn {
  transition: all 0.3s ease;
}

.favorite-btn:hover {
  transform: scale(1.2);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .user-card {
    margin-bottom: 0.5rem;
  }
  
  .logo-wrapper {
    width: 48px;
    height: 48px;
    padding: 9px;
  }
  
  .header-logo {
    height: 30px;
  }
}

@media (max-width: 600px) {
  .user-card .d-flex {
    padding: 12px !important;
  }

  .avatar-border {
    width: 48px !important;
    height: 48px !important;
    margin-right: 12px !important;
  }

  .text-subtitle-1 {
    font-size: 0.95rem !important;
  }

  .text-caption {
    font-size: 0.75rem !important;
  }
  
  .logo-wrapper {
    width: 44px;
    height: 44px;
    padding: 8px;
    margin-right: 12px;
  }
  
  .header-logo {
    height: 28px;
  }
}

@media (max-width: 400px) {
  .logo-wrapper {
    width: 40px;
    height: 40px;
    padding: 7px;
    margin-right: 10px;
  }
  
  .header-logo {
    height: 26px;
  }
}

/* Smooth scrolling */
.v-container {
  scroll-behavior: smooth;
}

/* Animation for list items */
.user-card {
  animation: fadeInUp 0.4s ease-out backwards;
}

.user-card:nth-child(1) { animation-delay: 0.05s; }
.user-card:nth-child(2) { animation-delay: 0.1s; }
.user-card:nth-child(3) { animation-delay: 0.15s; }
.user-card:nth-child(4) { animation-delay: 0.2s; }
.user-card:nth-child(5) { animation-delay: 0.25s; }
.user-card:nth-child(6) { animation-delay: 0.3s; }

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
